import React from 'react';
import styled from 'styled-components';

const VisualizerContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 30px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  min-height: 400px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 2px;
  position: relative;
`;

const Bar = styled.div`
  background: ${props => {
    if (props.isComparing) return '#ff6b6b';
    if (props.isSwapping) return '#4ecdc4';
    if (props.isSorted) return '#45b7d1';
    return '#f8f9fa';
  }};
  width: ${props => Math.max(20, 800 / props.arrayLength)}px;
  height: ${props => props.value * 3}px;
  border-radius: 4px 4px 0 0;
  transition: all 0.3s ease;
  position: relative;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

const BarValue = styled.div`
  position: absolute;
  top: -25px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  ${Bar}:hover & {
    opacity: 1;
  }
`;

const Legend = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 20px;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  font-size: 14px;
`;

const LegendColor = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: ${props => props.color};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: white;
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
  opacity: 0.7;
`;

const SortingVisualizer = ({ array, isSorting, currentAlgorithm }) => {
  if (!array || array.length === 0) {
    return (
      <VisualizerContainer>
        <EmptyState>
          <EmptyIcon>📊</EmptyIcon>
          <h3>No data to visualize</h3>
          <p>Generate an array to start visualizing sorting algorithms</p>
        </EmptyState>
      </VisualizerContainer>
    );
  }

  return (
    <div>
      <VisualizerContainer>
        {array.map((value, index) => (
          <Bar
            key={index}
            value={value}
            arrayLength={array.length}
            isComparing={isSorting}
            isSwapping={false}
            isSorted={false}
          >
            <BarValue>{value}</BarValue>
          </Bar>
        ))}
      </VisualizerContainer>
      
      <Legend>
        <LegendItem>
          <LegendColor color="#f8f9fa" />
          <span>Unsorted</span>
        </LegendItem>
        <LegendItem>
          <LegendColor color="#ff6b6b" />
          <span>Comparing</span>
        </LegendItem>
        <LegendItem>
          <LegendColor color="#4ecdc4" />
          <span>Swapping</span>
        </LegendItem>
        <LegendItem>
          <LegendColor color="#45b7d1" />
          <span>Sorted</span>
        </LegendItem>
      </Legend>
    </div>
  );
};

export default SortingVisualizer;
